import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    language: '',
    caseType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4pj6p9fcjnktac9qk20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          language: '',
          caseType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 bg-lavender-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Contact Us</p>
          <h2 className="text-6xl md:text-7xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Free <em className="italic font-normal">Consultation</em>
          </h2>
          <div className="w-16 h-px bg-gray-900 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: "'Crimson Pro', serif" }}>
            Take the first step toward justice. Fill out the form below and our team will contact you within 24 hours to discuss your case. All consultations are completely free and confidential.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="bg-lavender-50 rounded-none border border-gray-200 p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="firstName" className="block text-xs tracking-wider uppercase text-gray-500 mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-sm bg-transparent"
                    style={{ fontFamily: "'Crimson Pro', serif" }}
                    placeholder="John"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-xs tracking-wider uppercase text-gray-500 mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-sm bg-transparent"
                    style={{ fontFamily: "'Crimson Pro', serif" }}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="email" className="block text-xs tracking-wider uppercase text-gray-500 mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-sm bg-transparent"
                    style={{ fontFamily: "'Crimson Pro', serif" }}
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs tracking-wider uppercase text-gray-500 mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-sm bg-transparent"
                    style={{ fontFamily: "'Crimson Pro', serif" }}
                    placeholder="(317) 555-0123"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="language" className="block text-xs tracking-wider uppercase text-gray-500 mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Preferred Language *
                  </label>
                  <select
                    id="language"
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-sm bg-transparent cursor-pointer"
                    style={{ fontFamily: "'Crimson Pro', serif" }}
                  >
                    <option value="">Select Language</option>
                    <option value="English">English</option>
                    <option value="Spanish">Spanish (Español)</option>
                    <option value="Korean">Korean (한국어)</option>
                    <option value="Chinese">Chinese (中文)</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="caseType" className="block text-xs tracking-wider uppercase text-gray-500 mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Type of Case *
                  </label>
                  <select
                    id="caseType"
                    name="caseType"
                    value={formData.caseType}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-sm bg-transparent cursor-pointer"
                    style={{ fontFamily: "'Crimson Pro', serif" }}
                  >
                    <option value="">Select Case Type</option>
                    <option value="Car Accident">Car Accident</option>
                    <option value="Workplace Injury">Workplace Injury</option>
                    <option value="Medical Malpractice">Medical Malpractice</option>
                    <option value="Slip and Fall">Slip and Fall</option>
                    <option value="Wrongful Death">Wrongful Death</option>
                    <option value="Product Liability">Product Liability</option>
                    <option value="Dog Bite">Dog Bite</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-xs tracking-wider uppercase text-gray-500 mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Tell Us About Your Case * (Max 500 characters)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-sm resize-none bg-transparent"
                  style={{ fontFamily: "'Crimson Pro', serif" }}
                  placeholder="Please describe what happened and any injuries you sustained..."
                />
                <p className="text-xs text-gray-400 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gray-900 text-white py-4 text-sm tracking-widest uppercase hover:bg-gray-800 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}
              >
                {isSubmitting ? 'Submitting...' : 'Request Free Consultation'}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-[#e8f5e9] border border-[#a5d6a7]">
                  <p className="text-green-800 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Thank you! We&apos;ll contact you within 24 hours.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200">
                  <p className="text-red-800 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Something went wrong. Please call us at (317) 701-2768.</p>
                </div>
              )}

              <p className="text-xs text-gray-400 mt-4 text-center" style={{ fontFamily: "'Inter', sans-serif" }}>
                By submitting this form, you agree to our privacy policy. Your information is confidential and will never be shared.
              </p>
            </form>
          </div>

          <div className="space-y-8">
            <div className="bg-lavender-50 border border-gray-200 p-8">
              <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Reach Us</p>
              <h3 className="text-3xl font-light text-gray-900 mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
                Contact <em className="italic">Information</em>
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-gray-700 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-xs tracking-wider uppercase text-gray-500 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Phone</h4>
                    <a href="tel:+13177012768" className="text-gray-800 hover:text-gray-600 transition-colors text-sm" style={{ fontFamily: "'Crimson Pro', serif" }}>
                      (317) 701-2768
                    </a>
                    <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>Available 24/7 for emergencies</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-gray-700 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-xs tracking-wider uppercase text-gray-500 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Email</h4>
                    <a href="mailto:askus@junlawfirm.com" className="text-gray-800 hover:text-gray-600 transition-colors text-sm" style={{ fontFamily: "'Crimson Pro', serif" }}>
                      askus@junlawfirm.com
                    </a>
                    <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-gray-700 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-xs tracking-wider uppercase text-gray-500 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Office Location</h4>
                    <p className="text-gray-800 text-sm" style={{ fontFamily: "'Crimson Pro', serif" }}>
                      10150 Lantern Rd, Suite #175<br />
                      Fishers, IN 46037
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>Free parking available</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-gray-700 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-xs tracking-wider uppercase text-gray-500 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Office Hours</h4>
                    <p className="text-gray-800 text-sm" style={{ fontFamily: "'Crimson Pro', serif" }}>
                      Monday - Friday: 8:00 AM - 6:00 PM<br />
                      Saturday: 9:00 AM - 2:00 PM<br />
                      Sunday: By appointment only
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 p-8 text-white">
              <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Why Us</p>
              <h3 className="text-3xl font-light mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Why Choose <em className="italic">Jun Law Firm?</em>
              </h3>
              <div className="w-12 h-px bg-gray-600 mb-6"></div>
              <ul className="space-y-4">
                {[
                  'No fees unless we win your case',
                  'Multilingual staff and attorneys',
                  'Dedicated to immigrant communities',
                  'Free consultations and case evaluations'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-xs tracking-wider text-gray-500 mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>0{idx + 1}</span>
                    <span className="text-sm text-gray-300" style={{ fontFamily: "'Inter', sans-serif" }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
